import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { publicQuestionsService, PublicQuestion } from '../../services/publicQuestionsService';

export const AdminPublicQuestions: React.FC = () => {
  const [questions, setQuestions] = useState<PublicQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'answered' | 'published' | 'archived'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState<PublicQuestion | null>(null);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [answerVideo, setAnswerVideo] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchQuestions();
  }, [statusFilter]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      // Récupérer toutes les questions (sans pagination pour l'admin)
      let allQuestions: PublicQuestion[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const data = await publicQuestionsService.getAllQuestions(page);
        allQuestions = [...allQuestions, ...data.results];
        hasMore = !!data.next;
        page++;
      }

      setQuestions(allQuestions);
    } catch (error) {
      console.error('Erreur lors du chargement des questions:', error);
      setError('Erreur lors du chargement des questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async () => {
    if (!selectedQuestion) {
      setError('Aucune question sélectionnée');
      return;
    }

    // Vérifier qu'au moins une réponse (texte ou vidéo) est fournie
    if (!answerText.trim() && !answerVideo) {
      setError('Veuillez fournir une réponse (texte ou vidéo)');
      return;
    }

    // Si texte fourni, vérifier la longueur minimale
    if (answerText.trim() && answerText.trim().length < 10) {
      setError('La réponse texte doit contenir au moins 10 caractères');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      await publicQuestionsService.answerQuestion(
        selectedQuestion.id,
        answerText.trim() || undefined,
        answerVideo || undefined
      );
      setSuccess('Réponse ajoutée avec succès !');
      setAnswerText('');
      setAnswerVideo(null);
      setShowAnswerModal(false);
      setSelectedQuestion(null);
      fetchQuestions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Erreur lors de l\'ajout de la réponse:', error);
      setError(error.response?.data?.detail || 'Erreur lors de l\'ajout de la réponse');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async (question: PublicQuestion) => {
    try {
      await publicQuestionsService.publishQuestion(question.id);
      setSuccess('Question publiée avec succès !');
      fetchQuestions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Erreur lors de la publication:', error);
      setError(error.response?.data?.detail || 'Erreur lors de la publication');
    }
  };

  const handleArchive = async (question: PublicQuestion) => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver cette question ?')) {
      return;
    }
    try {
      await publicQuestionsService.archiveQuestion(question.id);
      setSuccess('Question archivée avec succès !');
      fetchQuestions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Erreur lors de l\'archivage:', error);
      setError(error.response?.data?.detail || 'Erreur lors de l\'archivage');
    }
  };

  const openAnswerModal = (question: PublicQuestion) => {
    setSelectedQuestion(question);
    setAnswerText(question.answer || '');
    setAnswerVideo(null);
    setShowAnswerModal(true);
    setError(null);
    setSuccess(null);
  };

  const closeAnswerModal = () => {
    setShowAnswerModal(false);
    setSelectedQuestion(null);
    setAnswerText('');
    setAnswerVideo(null);
    setError(null);
    setSuccess(null);
  };

  const filteredQuestions = questions.filter((q) => {
    const search = searchQuery.toLowerCase();
    return (
      q.question.toLowerCase().includes(search) ||
      (q.answer && q.answer.toLowerCase().includes(search)) ||
      q.author_name.toLowerCase().includes(search)
    );
  });

  const unansweredCount = questions.filter((q) => !q.answer).length;
  const answeredCount = questions.filter((q) => q.answer).length;
  const pendingCount = questions.filter((q) => q.status === 'pending').length;
  const publishedCount = questions.filter((q) => q.status === 'published').length;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string, hasAnswer: boolean) => {
    if (!hasAnswer) {
      return <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">⏳ En attente</span>;
    }
    switch (status) {
      case 'published':
        return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">✅ Publiée</span>;
      case 'answered':
        return <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">✅ Répondue</span>;
      case 'archived':
        return <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">📦 Archivée</span>;
      default:
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">⏳ En attente</span>;
    }
  };

  return (
    <AdminLayout activeSection="public-questions">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">❓ Questions Publiques</h1>
            <p className="text-gray-600 mt-2">Gérez et répondez aux questions posées par les utilisateurs</p>
          </div>
          <button
            onClick={fetchQuestions}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
          >
            🔄 Actualiser
          </button>
        </div>

        {/* Messages */}
        {success && (
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 animate-slide-down">
            <p className="text-green-700 font-medium">{success}</p>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 animate-slide-down">
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        )}

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 mb-1">{questions.length}</div>
              <div className="text-gray-600 text-sm">Total Questions</div>
            </div>
          </div>
          <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200 shadow-lg">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-700 mb-1">{unansweredCount}</div>
              <div className="text-yellow-600 text-sm">⏳ Sans Réponse</div>
            </div>
          </div>
          <div className="bg-green-50 rounded-xl p-6 border border-green-200 shadow-lg">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-700 mb-1">{answeredCount}</div>
              <div className="text-green-600 text-sm">✅ Répondues</div>
            </div>
          </div>
          <div className="bg-blue-50 rounded-xl p-6 border border-blue-200 shadow-lg">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-700 mb-1">{publishedCount}</div>
              <div className="text-blue-600 text-sm">📢 Publiées</div>
            </div>
          </div>
        </div>

        {/* Filtres et Recherche */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recherche */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par question, réponse ou auteur..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Filtre par statut */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filtrer par statut</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">Toutes</option>
                <option value="pending">⏳ En attente</option>
                <option value="answered">✅ Répondues</option>
                <option value="published">📢 Publiées</option>
                <option value="archived">📦 Archivées</option>
              </select>
            </div>
          </div>
        </div>

        {/* Liste des questions */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-lg">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-medium">Aucune question trouvée</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredQuestions.map((question) => (
                <div key={question.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Question */}
                      <div className="mb-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-xl">❓</span>
                          <span className="font-semibold text-gray-900 text-lg">{question.question}</span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 ml-7">
                          <span>Par: {question.author_name}</span>
                          <span>•</span>
                          <span>{formatDate(question.created_at)}</span>
                        </div>
                      </div>

                      {/* Réponse */}
                      {question.answer ? (
                        <div className="ml-7 mb-3 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-sm font-semibold text-green-700">✅ Réponse:</span>
                            {question.answered_by_name && (
                              <span className="text-xs text-gray-600">par {question.answered_by_name}</span>
                            )}
                            {question.answered_at && (
                              <span className="text-xs text-gray-500">le {formatDate(question.answered_at)}</span>
                            )}
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">{question.answer}</p>
                        </div>
                      ) : (
                        <div className="ml-7 mb-3 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
                          <p className="text-yellow-700 text-sm">⏳ En attente de réponse</p>
                        </div>
                      )}

                      {/* Statut */}
                      <div className="ml-7">
                        {getStatusBadge(question.status, !!question.answer)}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col space-y-2 ml-4">
                      {!question.answer && (
                        <button
                          onClick={() => openAnswerModal(question)}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          ✏️ Répondre
                        </button>
                      )}
                      {question.answer && !question.answer.includes(answerText) && (
                        <button
                          onClick={() => openAnswerModal(question)}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          ✏️ Modifier
                        </button>
                      )}
                      {question.status !== 'published' && question.answer && (
                        <button
                          onClick={() => handlePublish(question)}
                          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          📢 Publier
                        </button>
                      )}
                      {question.status !== 'archived' && (
                        <button
                          onClick={() => handleArchive(question)}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          📦 Archiver
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal pour répondre */}
      {showAnswerModal && selectedQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Répondre à la question</h2>
                <button
                  onClick={closeAnswerModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Question */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-600 text-sm mb-2">Question:</p>
                <p className="text-gray-900 font-medium">{selectedQuestion.question}</p>
                <p className="text-gray-500 text-xs mt-2">Par: {selectedQuestion.author_name} • {formatDate(selectedQuestion.created_at)}</p>
              </div>

              {/* Réponse texte */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Réponse texte <span className="text-gray-500">(optionnel si vidéo fournie, minimum 10 caractères)</span>
                </label>
                <textarea
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Tapez votre réponse texte ici..."
                />
                <p className="text-xs text-gray-500 mt-1">{answerText.length} caractères</p>
              </div>

              {/* Réponse vidéo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Réponse vidéo <span className="text-gray-500">(optionnel si texte fourni)</span>
                </label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Vérifier la taille (max 100MB)
                      if (file.size > 100 * 1024 * 1024) {
                        setError('La vidéo ne doit pas dépasser 100MB');
                        return;
                      }
                      setAnswerVideo(file);
                      setError(null);
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                {answerVideo && (
                  <p className="text-xs text-green-600 mt-1">
                    📹 Vidéo sélectionnée : {answerVideo.name} ({(answerVideo.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
                {selectedQuestion?.answer_video && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-600 mb-1">Vidéo actuelle :</p>
                    <video
                      controls
                      className="w-full max-w-md rounded-lg"
                      src={selectedQuestion.answer_video}
                    >
                      Votre navigateur ne supporte pas la lecture de vidéos.
                    </video>
                  </div>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-4">
              <button
                onClick={closeAnswerModal}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleAnswer}
                disabled={submitting || (!answerText.trim() && !answerVideo) || (!!answerText.trim() && answerText.trim().length < 10)}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Enregistrement...' : '✅ Enregistrer la réponse'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

